import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { RefinanceState } from '@/store/slices/refinanceSlice';
import { calculateHELOANClosingDisclosure } from '@/utils/calculator/HELOAN';
import { calculateHELOCClosingDisclosure } from '@/utils/calculator/HELOC';
import { calculateSecondMortgageClosingDisclosure } from '@/utils/calculator/SecondMortgage';
import {
  calculateBridgeRefinanceClosingDisclosure,
  calculateCommercialClosingDisclosure,
  calculateConventionalClosingDisclosure,
  calculateDefaultClosingDisclosure,
  calculateDSCRClosingDisclosure,
  calculateFHAClosingDisclosure,
  calculateFixAndFlipClosingDisclosure,
  calculateUSDAClosingDisclosure,
  calculateVAClosingDisclosure,
} from '@/utils/refinanceCalculator';
import type { Product } from '@/types/ProductType';

export function newLoanAmountFromRefinance(
  ref: NonNullable<Product['refinance']>
): number {
  const refiType = ref.selection?.refiType;
  const balance = ref.currentLoan?.balance ?? 0;
  if (['heloc', 'heloan', 'second_mortgage'].includes(String(refiType || ''))) {
    return ref.newLoan?.requestedLoanAmount || balance;
  }
  if (refiType === 'cash_out') {
    return balance + (ref.newLoan?.cashOutAmount || 0);
  }
  return balance;
}

export function getRefinanceImpliedEquityDown(product: Product): number {
  const ref = product.refinance;
  if (!ref) return 0;
  const value = ref.property?.value ?? product.price ?? 0;
  return Math.max(0, value - newLoanAmountFromRefinance(ref));
}

/**
 * Closing-disclosure-style estimate for a refinance product, aligned with
 * RefinanceSummaryPage / refinance calculators (not purchase calculateClosingDisclosure).
 */
const EQUITY_PROGRAMS = new Set<string>([
  USER_LOAN_PROGRAM.HELOC,
  USER_LOAN_PROGRAM.HELOAN,
  USER_LOAN_PROGRAM.SECOND_MORTGAGE,
]);

/**
 * Refinance estimate for a saved property. The purchase calculators report
 * cash to close as closing costs + down payment − earnest money; on a
 * refinance the "down payment" is just the owner's equity, so a rate/term
 * refi showed ~$200k due at closing. For first-lien refinances cash to close
 * is closing costs + payoff of the current loan − the new loan (negative =
 * cash back to the borrower).
 */
export function estimateRefinanceProduct(
  product: Product,
  state: RootState,
  overrides?: { loanProgram?: string; downPayment?: number }
) {
  const est = estimateRefinanceProductRaw(product, state, overrides);
  if (!est) return est;
  const program = String((est as { type?: string }).type || '').toLowerCase();
  if (EQUITY_PROGRAMS.has(program)) return est;

  const raw = est as Record<string, unknown>;
  const closingCosts = Number(raw.closingCosts ?? raw.totalLenderCosts) || 0;
  const payoff = product.refinance?.currentLoan?.balance ?? 0;
  const newLoan = Number(raw.loanAmount) || 0;
  const cashToClose = closingCosts + payoff - newLoan;
  return {
    ...est,
    earnestMoneyDeposit: 0,
    cashToClose,
    unbeatableDeal: cashToClose,
    refinanceCashFlow: {
      payoff,
      newLoan,
      closingCosts,
      cashToClose,
      // Without the balance the payoff reads $0 and the whole new loan looks
      // like cash back.
      payoffKnown: product.refinance?.currentLoan?.balance != null,
    },
  };
}

function estimateRefinanceProductRaw(
  product: Product,
  state: RootState,
  overrides?: { loanProgram?: string; downPayment?: number }
) {
  const ref = product.refinance;
  if (!ref || product.transactionMode !== 'refinance') return null;

  const value = ref.property?.value ?? product.price ?? 0;
  const newLoanAmount = newLoanAmountFromRefinance(ref);
  const impliedEquityDown = Math.max(0, value - newLoanAmount);
  const downPayment =
    overrides?.downPayment !== undefined
      ? overrides.downPayment
      : impliedEquityDown;

  const programRaw =
    overrides?.loanProgram ||
    product.savedLoanType ||
    ref.selection?.program ||
    USER_LOAN_PROGRAM.CONVENTIONAL;
  const program = String(programRaw).toLowerCase() as USER_LOAN_PROGRAM;

  // Size of a HELOC / HELOAN / 2nd mortgage. Never the first mortgage's
  // balance — that priced a $300k home-equity loan for a $50k request.
  const equityLineOrLoan = ref.newLoan?.requestedLoanAmount || undefined;

  const zipStr = ref.property?.zip || product.address?.zip || '';
  const estimationInput = {
    offerPrice: value,
    listPrice: value,
    downPayment,
    zipCode: Number(String(zipStr).replace(/\D/g, '').slice(0, 5)) || 0,
    hoaFees: ref.currentLoan?.escrowHoa ?? 0,
    commission: 0,
    marketInterestRate: ref.currentLoan?.rate ?? 6.75,
    creditScore: ref.newLoan?.creditScore ?? 700,
    loanTerm: ref.newLoan?.termYears ?? 30,
    currentLoanBalance: ref.currentLoan?.balance ?? 0,
    requestedLoanAmount: equityLineOrLoan,
    requestedCreditLine: equityLineOrLoan,
    // DSCR prices on the property's rent; FHA/VA/USDA on the borrower's DTI.
    rent: product.loanInfo?.rent,
    borrowerDebtProfile: {
      monthlyIncome: product.user?.loanInfo?.monthlyIncome,
      monthlyDebt: product.user?.loanInfo?.monthlyDebt,
    },
  };

  switch (program) {
    case USER_LOAN_PROGRAM.CONVENTIONAL:
      return calculateConventionalClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.FHA:
      return calculateFHAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.VA:
      return calculateVAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.USDA:
      return calculateUSDAClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.DSCR:
      return calculateDSCRClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.FIX_AND_FLIP:
      return calculateFixAndFlipClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.COMMERCIAL:
      return calculateCommercialClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.BRIDGE: {
      const totalLoan =
        estimationInput.offerPrice - estimationInput.downPayment;
      const existingLoanBalance = ref.currentLoan?.balance ?? 0;
      const cashOutAmount = Math.max(0, totalLoan - existingLoanBalance);
      const loanTermMonths = Math.min(
        24,
        Math.max(6, estimationInput.loanTerm * 12)
      );
      return calculateBridgeRefinanceClosingDisclosure(
        {
          currentPropertyValue: estimationInput.offerPrice,
          existingLoanBalance,
          cashOutAmount,
          zipCode: estimationInput.zipCode,
          hoaFees: estimationInput.hoaFees,
          marketInterestRate: estimationInput.marketInterestRate,
          creditScore: estimationInput.creditScore,
          loanTerm: loanTermMonths,
        },
        state
      );
    }
    case USER_LOAN_PROGRAM.HELOC:
      return calculateHELOCClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.HELOAN:
      return calculateHELOANClosingDisclosure(estimationInput, state);
    case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
      return calculateSecondMortgageClosingDisclosure(estimationInput, state);
    default:
      return calculateDefaultClosingDisclosure(estimationInput, state);
  }
}

/**
 * Merge live Redux refinance intake with the API product so hooks like useEstimation
 * see the same numbers as the refinance summary header cards.
 */
export function mergeRefinanceSliceIntoProduct(
  product: Product,
  slice: RefinanceState
): Product {
  const zipDigits = String(slice.property.zipCode || '').replace(/\D/g, '');
  const zipNum = zipDigits ? parseInt(zipDigits.slice(0, 5), 10) : undefined;

  return {
    ...product,
    transactionMode: 'refinance',
    price: slice.property.value || product.price,
    savedLoanType: slice.selection.program,
    loanInfo: {
      ...product.loanInfo,
      offerPrice:
        slice.property.value || product.loanInfo?.offerPrice || product.price,
      ...(zipNum != null && !Number.isNaN(zipNum) ? { zipCode: zipNum } : {}),
    },
    refinance: {
      ...product.refinance,
      currentLoan: {
        rate: slice.currentLoan.rate,
        balance: slice.currentLoan.balance,
        termYears: slice.currentLoan.termYears,
        monthlyPayment: slice.currentLoan.monthlyPayment,
        escrowTaxes: slice.currentLoan.escrowTaxes,
        escrowInsurance: slice.currentLoan.escrowInsurance,
        escrowHoa: slice.currentLoan.escrowHoa,
      },
      newLoan: {
        termYears: slice.newLoan.termYears,
        creditScore: slice.newLoan.creditScore,
        cashOutAmount: slice.newLoan.cashOutAmount,
        requestedLoanAmount: slice.newLoan.requestedLoanAmount,
      },
      property: {
        street: slice.property.address,
        city: slice.property.city,
        state: slice.property.state,
        zip: slice.property.zipCode,
        fullAddress: product.address?.fullAddress,
        value: slice.property.value,
        propertyType: slice.property.propertyType,
      },
      selection: {
        program: slice.selection.program,
        refiType: slice.selection.refiType,
        occupancy: slice.selection.occupancy,
      },
      statement: {
        escrowBalance: slice.statement.escrowBalance,
        buydownBalance: slice.statement.buydownBalance,
        prepaymentPenalty: slice.statement.prepaymentPenalty,
        lastPaymentDate: slice.statement.lastPaymentDate,
        estimatedPayoff: slice.statement.estimatedPayoff,
      },
    },
  };
}
